// ENG-9334 — how old is this alert, and does its age change what it means?
//
// THE OBSERVATION. Across five consecutive hourly fleet sweeps the same
// stalled agent was reported five times, at identical severity, in an
// identical shape, while the underlying condition monotonically worsened.
// Nothing intervened. The alert carried no signal that would make a human
// treat the eleventh hour differently from the first.
//
// Re-checked 2026-08-29, seven days after the ticket was filed, and it had got
// worse rather than better:
//
//   gccm-host   auth_failed    open since  9 Aug — 20 days, never acknowledged
//   valai       agent_stale    open since 14 Aug — 15 days
//   sherlock    agent_paused   open since late July
//   sterling    agent_paused   open since late July
//   monster     agent_paused   open since 21 Aug, unacknowledged throughout
//
// Every one of those renders on the board exactly like an alert that opened
// four minutes ago: `new Date(openedAt).toLocaleString()`, an absolute
// timestamp the reader has to subtract by hand.
//
// WHY THIS IS IN CORE. The same question — "how old, and does that change the
// severity" — is asked in two places that cannot import each other: the pager
// (`packages/api`) deciding whether to escalate, and the console
// (`packages/webapp`) deciding what to draw. ENG-9546 is the cautionary tale
// directly next door: `closesOnAck` lived in `packages/api`, the webapp could
// not import it, so the set was COPIED, and the copy diverged and stranded
// exactly the backlog it was written to end. One definition, both sides.
//
// WHAT THIS MODULE DELIBERATELY DOES NOT DO
//
// It does not write `severity`. That was the obvious implementation and it is
// a trap: `open_alert` upserts with `DO UPDATE SET severity = EXCLUDED.severity`,
// and the monitors pass a hardcoded value (`agent-stall-monitor.ts` raises
// `agent_stall` as `'warning'` on every tick). A promotion written to the row
// therefore survives only until the next monitor tick, which for a stall
// monitor is minutes. An ager that wrote the column would appear to work in a
// test and do nothing at all in production.
//
// So the column keeps meaning "what the monitor observed", which is the only
// thing the monitor can honestly assert, and AGE IS APPLIED BY THE READER.
// Both readers, through this one function.
//
// It also does not remove the re-arm generation cap. `alert-rearm.ts` states
// that cap as a considered decision — "the antidote to an unread critical is
// not an unreadable channel" — with the fleet unacknowledged view as the
// intended backstop. The evidence above shows that backstop is not working,
// but that is a separate change. What this module does instead is make an age
// crossing look like what it actually is: NEW INFORMATION. ENG-8364 already
// exempts a severity increase from the cap, because a promotion is not a
// repeat. An alert that has aged from "worth knowing" into "nobody is coming"
// is making a different claim than the one that was already paged and ignored.

/** Ordered severity ladder. Mirrors `SEVERITY_RANK` in `api/lib/alert-rearm.ts`. */
const RANK: Readonly<Record<string, number>> = { info: 0, warning: 1, critical: 2 };

const BY_RANK = ['info', 'warning', 'critical'] as const;

/** A severity as stored on `alerts.severity` (DB CHECK enforces these three). */
export type AlertSeverityName = (typeof BY_RANK)[number];

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * Age thresholds at which an OPEN alert's meaning changes.
 *
 * These are floors, not overrides — see `effectiveAlertSeverity`. An alert
 * never ages DOWN, so a critical stays critical no matter what this table says.
 *
 * The numbers are deliberately aligned to the re-arm cadence already in use
 * (`DEFAULT_REARM_CRITICAL_SECONDS` is 12h) rather than invented: a warning
 * that has outlived two full critical re-arm cycles without anyone closing it
 * is not being triaged, it is being ignored. 24h is that point.
 *
 * `info` is left alone entirely. It is the dashboard-only tier — the pager
 * skips it before it reaches any of this — and quietly ageing a
 * dashboard-only row into a page is how an escalation ladder earns the
 * distrust that makes people mute the channel.
 */
export const ALERT_AGE_SEVERITY_FLOORS: readonly {
  readonly afterMs: number;
  readonly floor: AlertSeverityName;
}[] = [{ afterMs: 24 * HOUR_MS, floor: 'critical' }];

/**
 * Age of an open alert in milliseconds, or null when it cannot be established.
 *
 * Null, never 0, on an unparseable or future `opened_at`. Zero would read as
 * "brand new" and is the more dangerous wrong answer: it silently withholds
 * escalation from precisely the rows whose timestamps are broken. Callers must
 * decide what to do with "unknown" rather than being handed a plausible lie.
 *
 * A future `opened_at` is treated as unknown rather than clamped to 0. Clock
 * skew is real, and an alert claiming to have opened tomorrow is a row to
 * distrust, not to reason about.
 */
export function alertAgeMs(openedAt: string | null | undefined, nowMs: number): number | null {
  if (!openedAt) return null;
  const openedMs = Date.parse(openedAt);
  if (!Number.isFinite(openedMs)) return null;
  const age = nowMs - openedMs;
  if (age < 0) return null;
  return age;
}

/**
 * The severity a reader should ACT on: the stored severity, raised (never
 * lowered) by how long the alert has been open.
 *
 * `kind` is taken because point-in-time kinds are exempt. ENG-9434 established
 * that distinction: a condition alert has a monitor that re-observes the world,
 * so its age measures how long the condition has persisted. A point-in-time
 * alert's age measures only how long ago something happened that nothing can
 * now refute, and escalating on that is escalating on the passage of time
 * rather than on the world getting worse. `impersonation_marker_outlived`
 * accumulated 28 open rows precisely because it had no terminal state; ageing
 * them into criticals would have paged for every one.
 *
 * Returns the stored severity unchanged when the age is unknown. Escalate only
 * on positive evidence — the same fail-safe direction `computeRearm` takes.
 */
export function effectiveAlertSeverity(input: {
  kind: string;
  severity: string;
  openedAt: string | null | undefined;
  nowMs: number;
  /** Kinds whose age carries no information. Pass `POINT_IN_TIME_ALERT_KINDS`. */
  pointInTimeKinds: ReadonlySet<string>;
}): string {
  const { kind, severity, openedAt, nowMs, pointInTimeKinds } = input;

  const storedRank = RANK[severity];
  // An unrecognised severity is not something to reason about. Hand it back
  // untouched rather than guessing a rank for it — `computeRearm` already
  // refuses to act on one, and inventing a rank here would defeat that.
  if (storedRank === undefined) return severity;

  if (pointInTimeKinds.has(kind)) return severity;
  if (severity === 'info') return severity;

  const age = alertAgeMs(openedAt, nowMs);
  if (age === null) return severity;

  let rank = storedRank;
  for (const { afterMs, floor } of ALERT_AGE_SEVERITY_FLOORS) {
    if (age >= afterMs) rank = Math.max(rank, RANK[floor] ?? rank);
  }
  return BY_RANK[rank] ?? severity;
}

/** True when age alone raised this alert above what the monitor recorded. */
export function isAgeEscalated(input: {
  kind: string;
  severity: string;
  openedAt: string | null | undefined;
  nowMs: number;
  pointInTimeKinds: ReadonlySet<string>;
}): boolean {
  return effectiveAlertSeverity(input) !== input.severity;
}

/**
 * Human-readable age: `4m`, `11h`, `20d`.
 *
 * ONE UNIT, NOT TWO. "20d" beats "20d 4h 12m" for the job this does, which is
 * to be scanned down a column of thirty rows next to a "4m". The precision a
 * second unit adds is precision nobody reading a triage queue needs, and it
 * costs the instant comparison that is the entire point of the field.
 *
 * Sub-minute ages render as `<1m` rather than a seconds count, so a fresh row
 * reads as "just now" instead of ticking distractingly between renders.
 *
 * Returns null for an unknown age so callers render their own placeholder
 * rather than this module inventing an em dash for someone else's table.
 */
export function formatAlertAge(ageMs: number | null): string | null {
  if (ageMs === null || !Number.isFinite(ageMs) || ageMs < 0) return null;
  if (ageMs < MINUTE_MS) return '<1m';
  if (ageMs < HOUR_MS) return `${Math.floor(ageMs / MINUTE_MS)}m`;
  if (ageMs < DAY_MS) return `${Math.floor(ageMs / HOUR_MS)}h`;
  return `${Math.floor(ageMs / DAY_MS)}d`;
}

/**
 * Convenience for a renderer that has the timestamp rather than a duration.
 * Null when the age is unknown.
 */
export function formatAlertAgeSince(
  openedAt: string | null | undefined,
  nowMs: number,
): string | null {
  return formatAlertAge(alertAgeMs(openedAt, nowMs));
}
